<template>
  <div class="sd-product-manual">
    <div class="sd-product-manual__content-actions"
         v-show="showTutorialPrevNext">
      <div class="sd-button-group">
        <button class="sd-button sd-button_outl_sm tutorial-button"
                v-if="enablePrevTutorial"
                @click="prevTutorial">Prev: {{prevTutorialTitle}}</button>
        <button class="sd-button sd-button_outl_sm tutorial-button"
                v-if="enableNextTutorial"
                @click="nextTutorial">Next: {{nextTutorialTitle}}</button>
      </div>
    </div>
    <div class="sd-product-manual__content"
         :class="{'show-full-description': showFullDescription}">
      <div class="sd-product-manual__content-main">
        <div class="sd-product-manual__content-main_heading"
             :class="{'show-full-description': descriptionOverflowing}"
             v-if="currentTutorialStep.instruction.title"
             @tap="toggleFullDescription"
             @click="toggleFullDescription">
          <h3 class="s2_25vp current-step"
              v-if="currentTutorialStep.type !== 'intro'">
            <span class="numerator">&#x2116;{{currentTutorialStepIndex}}</span>
          </h3>
          <h3 class="tutorial-step-title">{{currentTutorialStep.instruction.title}}</h3>
          <button class="sd-button sd-button_icon_lt_sm dropdown__toggle show-full-description__toggle"
                  v-show="descriptionOverflowing"
                  tabindex="-1">
            <more-vertical-icon v-if="!showFullDescription"></more-vertical-icon>
            <x-icon v-if="showFullDescription"></x-icon>
          </button>
        </div>
        <div class="description"
             @tap="toggleFullDescription"
             @click="toggleFullDescription"
             v-html="currentTutorialStep.instruction.text">
        </div>
        <div class="sd-product-manual__content-actions" v-show="enableNextTutorialStep">
          <div class="sd-button-group">
            <button class="sd-button sd-button_dk_sm"
                    v-show="enableNextTutorialStep"
                    @click="nextTutorialStep">
              <span v-if="currentTutorialStepIndex === 0">Begin</span>
              <span v-else>Next</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="sd-product-manual__diagram"
         :class="{'centered-diagram': showTutorialPrevNext}">
      <div class="sd-product-manual__interactive-product" :class="model.slug">
        <sd-watch
          class="sd-product-model"
          :ref="modelName"
          :data="watchModel"
          :tutorialModeData="tutorialMode"
          :tutorialStepData="currentTutorialStep"
          @evaluate-component="evaluateComponent">
        </sd-watch>
      </div>
    </div>

  </div>
</template>

<script>
// IntersectionObserver Polyfill (https://github.com/w3c/IntersectionObserver/tree/master/polyfill)
import 'intersection-observer'
import tutorialService from '@/services/tutorialService';
import manualService from '@/services/manualService';
import sdWatch from '@/components/models/Watch';
import { MoreVerticalIcon, XIcon } from 'vue-feather-icons';
import {
  camelCase, concat, every, find, has, includes,
} from 'lodash';
import { DateTime, Duration, Interval } from 'luxon';
import { mapGetters, mapActions } from 'vuex';

// TODO: Make tutorial state accessible via URL (e.g. Step two of first tutorial for A-36)

export default {
  name: 'ProductManual',
  components: {
    sdWatch,
    MoreVerticalIcon,
    XIcon
    // Intersect
  },
  props: [
    'modelName',
    // productModel is model info via Contentful
    'productModel',
    'title',
  ],
  data() {
    const self = this;
    return {
      get model() {
        return manualService.models.find(model => model.id === camelCase(self.modelName));
      },
      currentTutorialIndex: 0,
      currentTutorialStepIndex: 0,
      descriptionObserver: undefined,
      showFullDescription: false,
      descriptionIntersectionRatio: 1,
      root: undefined,
      rootHeight: undefined,
      contentMain: undefined
    };
  },
  computed: {
    ...mapGetters([
      'productModels',
    ]),
    watchModel() {
      if (this.model) return this.model;
    },
    tutorialMode() {
      return !!this.currentTutorialStep;
    },
    modelData() {
      if (this.productModels) {
        let _data = this.productModels.find(pm => pm.fields.slug === this.modelName);
        this.$emit('product-model', _data);
        return _data;
      }
    },
    // Returns all tutorials applicable to current product model
    tutorialsForModel() {
      return tutorialService.tutorials.filter(tutorial => includes(tutorial.models, this.watchModel.id), this);
    },
    currentTutorial() {
      if (this.tutorialsForModel) {
        const tutorial = this.tutorialsForModel[this.currentTutorialIndex];
        const _tutorial = {
          ...tutorial,
          steps: tutorial.steps.filter(step => (has(step, 'for') ? includes(step.for, this.watchModel.id) : true), this),
        };
        this.$emit('current-tutorial', _tutorial)
        return _tutorial
      }
    },
    currentTutorialStepsCount() {
      if (this.currentTutorial) return this.currentTutorial.steps.length;
    },
    currentTutorialStep() {
      if (this.currentTutorial && this.model) {
        this.currentTutorial.steps[this.currentTutorialStepIndex].instruction.model = this.model.id;
        return this.currentTutorial.steps[this.currentTutorialStepIndex];
      }
    },
    nextTutorialTitle() {
      if (this.tutorialsForModel && this.tutorialsForModel.length > 0) {
        return this.tutorialsForModel[(this.currentTutorialIndex + 1) % this.tutorialsForModel.length].title;
      }
    },
    prevTutorialTitle() {
      if (this.tutorialsForModel) {
        return this.tutorialsForModel[(((this.currentTutorialIndex - 1) % this.tutorialsForModel.length) + this.tutorialsForModel.length) % this.tutorialsForModel.length].title;
      }
    },
    enableNextTutorial() {
      if (this.tutorialsForModel && this.currentTutorialStepsCount) {
        return (this.tutorialsForModel.length > 0
            && this.currentTutorialIndex !== this.tutorialsForModel.length - 1
            && this.currentTutorialStepIndex === 0)
            || (this.tutorialsForModel.length > 0
              && this.currentTutorialIndex !== this.tutorialsForModel.length - 1
              && this.currentTutorialStepIndex === this.currentTutorialStepsCount - 1);
      }
    },
    enablePrevTutorial() {
      if (this.tutorialsForModel && this.currentTutorialStepsCount > 0) {
        return (this.tutorialsForModel.length > 1
            && this.currentTutorialIndex !== 0
            && this.currentTutorialStepIndex === 0)
            || (this.tutorialsForModel.length > 1
              && this.currentTutorialIndex !== 0
              && this.currentTutorialStepIndex === this.currentTutorialStepsCount - 1);
      }
    },
    showTutorialPrevNext() {
      return this.modelData && this.enablePrevTutorial || this.modelData && this.enableNextTutorial;
    },
    enableNextTutorialStep() {
      if (this.currentTutorial && this.currentTutorialStep) {
        return this.currentTutorialStep.type === 'intro'
            && (this.currentTutorialStepIndex !== this.currentTutorial.steps.length - 1);
      }
    },
    descriptionOverflowing() {
      return this.descriptionIntersectionRatio < 1
        ? true
        : this.showFullDescription === true
    }
  },
  methods: {
    ...mapActions([
      'getProductModels',
    ]),
    currentTutorialStepExpectationsMet(model) {
      let expectationsMet = [];
      if (this.currentTutorialStep.expectation.components) {
        expectationsMet = concat(expectationsMet, this.currentTutorialStep.expectation.components.map(component => component.value === find(this.model.components, (c) => {
          component.model = this.model.id;
          return c.id === component.id;
        }).value));
      }
      const timeTargets = [
        { name: 'time', value: model.now },
        { name: 'day', value: model.day },
        { name: 'weekday', value: model.weekday },
      ];
      timeTargets.forEach((target) => {
        if (this.currentTutorialStep.expectation[target.name]) {
          // Add a Boolean to expectationsMet[] indicating if 'target' falls within expected margin
          const interval = this.createInterval(
            this.currentTutorialStep.expectation[target.name].value,
            this.currentTutorialStep.expectation[target.name].margin,
          );
          const targetMet = target.name === 'time'
            ? interval.contains(target.value)
            : interval.contains(DateTime.fromObject({ [target.name]: target.value }));
          expectationsMet = concat(expectationsMet, targetMet);
        }
      }, this);
      if (this.currentTutorialStep.expectation.counterReset) {
        const targetsMet = Object.keys(this.currentTutorialStep.expectation.counterReset).map(key => this.currentTutorialStep.expectation.counterReset[key] === model.counterReset[key], this);
        expectationsMet = concat(expectationsMet, targetsMet);
      }
      if (this.currentTutorialStep.expectation.chronoZero) {
        // Check to see if the current counter hand is in its correct position
        const targetsMet = Object.keys(this.currentTutorialStep.expectation.chronoZero).map((key) => {
          if (this.model.id === 'a22' && key === 'hours') {
            return this.model.chrono.zero[key] % 12 === this.currentTutorialStep.expectation.chronoZero[key];
          }
          return this.model.chrono.zero[key] === this.currentTutorialStep.expectation.chronoZero[key];
        }, this);
        expectationsMet = concat(expectationsMet, targetsMet);
      }
      return expectationsMet.length > 0 ? every(expectationsMet, e => e === true) : true;
    },
    evaluateComponent(model) {
      if (this.currentTutorialStep.expectation && this.currentTutorialStepExpectationsMet(model)) {
        // Proceed to next step, if one
        if ((this.currentTutorialStepIndex + 1) % this.currentTutorialStepsCount !== 0) {
          this.nextTutorialStep();
        }
      }
    },
    nextTutorial() {
      // Reset tutorial step index
      this.currentTutorialStepIndex = 0;
      this.currentTutorialIndex = (this.currentTutorialIndex + 1) % this.tutorialsForModel.length;
      document.querySelectorAll('.tutorial-button').forEach(button => button.blur());
    },
    prevTutorial() {
      // Reset tutorial step index
      this.currentTutorialStepIndex = 0;
      this.currentTutorialIndex = (((this.currentTutorialIndex - 1) % this.tutorialsForModel.length)
          + this.tutorialsForModel.length) % this.tutorialsForModel.length;
      document.querySelectorAll('.tutorial-button').forEach(button => button.blur());
    },
    nextTutorialStep() {
      this.toggleFullDescription(null, false);
      this.currentTutorialStepIndex = (this.currentTutorialStepIndex + 1) % this.currentTutorialStepsCount;
    },
    createInterval(value, margin) {
      const val = value.isValid ? value : DateTime.fromObject(value);
      const dur = Duration.fromObject(margin);
      return Interval.merge([
        Interval.before(val, dur),
        Interval.after(val, dur),
      ])[0];
    },
    toggleFullDescription(e, override) {
      this.showFullDescription = override || !this.descriptionOverflowing
        ? false
        : !this.showFullDescription;
    },
    observeDescription() {
      this.showFullDescription = false;
      this.root = this.root !== undefined ? this.root : this.$el.querySelector('.sd-product-manual__content');
      this.contentMain = this.contentMain !== undefined ? this.contentMain : this.root.querySelector('.sd-product-manual__content-main');

      this.descriptionObserver = new IntersectionObserver(([entry]) => {
        this.setIntersectionRatio(entry.intersectionRatio)
      }, {root: this.root, threshold: 1})
      this.descriptionObserver.observe(this.contentMain)
    },
    setIntersectionRatio (val) {
      if (typeof val === 'boolean') val === true
        ? this.descriptionIntersectionRatio = 1
        : 0.5
      else this.descriptionIntersectionRatio = val
    }
  },
  created() {
    this.$emit('hide-main-nav');
    this.$emit('set-border', { color: 'blue' });
    if (!this.productModel || !this.productModels) {
      this.getProductModels();
    }
  },
  mounted() {
    this.evaluateComponent(this.model);
    this.observeDescription();
  },
  beforeDestroy() {
    this.$emit('show-main-nav');
  },
  destroyed() {
    this.descriptionObserver.disconnect()
  },
};
</script>

<style lang="scss" scoped>
  @import './../styles/typography';

  .sd-product-manual__diagram {
    background: #FFDEAD;
    box-shadow: 100vw 0 0 100vw #FFDEAD;
    margin-top: auto;
  }
  .tutorial-step-title {
    @extend .s2_25vp;
    line-height: 0.88;
  }
  .sd-product-manual__content-main_heading {
    & > h3 + h3 {
      margin-left: 1ch;
    }
  }
  .sd-product-manual__content-main_heading,
  .description {
    flex: 1 1 50%;
  }
  .tutorial-subtitle {
    display: flex;
  }
  .tutorial-subtitle > * + * {
    margin-left: 1ch;
  }
  @media all and (max-width: 599px) {
    .sd-product-manual {
      background: #FFDEAD;
      /*background: #EDEBB9;*/
      box-shadow: 0 0 0 48px #FFDEAD;
    }
    .sd-product-manual__interactive-product {
      flex: 0 0 auto;
      height: auto;
    }
    .tutorial-subtitle {
      width: 100%;
      display: flex;
      margin-bottom: 0.5ch;
      h1, .back-to-manuals {
        flex: 2;
      }
      h3 {
        flex: 1;
        text-align: center;
      }
      & > * + * {
        margin-left: 0;
      }
    }
    .tutorial-subtitle > * {
      font-size: 10.6px;
    }
    .tutorial-title {
      padding: 12px 12px 0;
    }
    .tutorial-title h2.s2_25vp,
    .tutorial-step-title {
      font-size: 24px;
      font-weight: 400;
      letter-spacing: -0.01em;
      line-height: 0.88;
    }
    .tutorial-title h2.s2_25vp {
      font-weight: 700;
      letter-spacing: 0;
    }
    .sd-product-manual__diagram {
      margin-top: 0;
    }
  }
</style>
